// Closing tab Step 2 (Payment Setup Status).
//
// Read-only status of each party's (Borrower / Seller / Broker) linked portal
// account + Stripe-sourced payment-method setup. The manual ACH bank-field
// capture + sign ceremony is retired: banking details are collected by the
// party themselves in their portal (a Stripe mandate / payout account), so
// staff only re-link the account, watch setup status, and nudge with a
// reminder. Nothing here is a hard gate on loan creation. NOTE: once a real,
// ready servicer (TPS) is assigned to the deal, the buyer CAN complete their
// bank setup as final pre-loan — it's captured directly on that servicer's
// clearing account and carries forward at boarding with no re-authorization,
// so the Borrower row reads "Complete" (not "Set up (pre-loan)").

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bell, Pencil, RotateCcw, Server } from "lucide-react";
import { Badge, Button, Card, StatusBadge } from "@/design-system/components/ui";
import type { StatusTone } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useDateFormatter } from "@/utils/datetime";
import { portalAccountsApi } from "@/api/portalAccounts";
import type { MaterialAudience } from "@/api/portalMaterials";
import {
  dealFlowKeys,
  useDealPaymentSetup,
  useDealTpsAssignment,
  useDealTpsOptions,
  useRelinkDealPartyAccount,
  useRemindDealParty,
  useSetDealTps,
} from "@/hooks/dealFlow/useDealFlow";
import type {
  DealPaymentSetupParty,
  DealPaymentSetupPartyDto,
  DealPaymentSetupStatus,
  DealTpsAssignment,
} from "@/api/dealFlow/types";

interface PaymentSetupStepProps {
  dealId: string;
  readOnly?: boolean;
}

const PARTY_ORDER: readonly DealPaymentSetupParty[] = [
  "Borrower",
  "Seller",
  "Broker",
] as const;

/** Which portal-account type backs each party's link picker. */
const PARTY_ACCOUNT_TYPE: Record<DealPaymentSetupParty, MaterialAudience> = {
  Borrower: "Buyer",
  Seller: "Seller",
  Broker: "Broker",
};

const STATUS_META: Record<
  DealPaymentSetupStatus,
  { tone: StatusTone; label: string }
> = {
  not_linked: { tone: "muted", label: "Not linked" },
  not_started: { tone: "warn", label: "Awaiting setup" },
  in_progress: { tone: "progress", label: "In progress" },
  // Track C — borrower-only sub-states around the pre-loan bank capture.
  captured_preloan: { tone: "progress", label: "Set up (pre-loan)" },
  reauthorize_needed: { tone: "warn", label: "Re-authorize pending" },
  servicer_changed: { tone: "warn", label: "Servicer changed — reconnect" },
  pending_servicer: { tone: "muted", label: "Pending servicer onboarding" },
  complete: { tone: "ok", label: "Complete" },
};

export function PaymentSetupStep({
  dealId,
  readOnly = false,
}: PaymentSetupStepProps) {
  const query = useDealPaymentSetup(dealId);
  const parties = orderParties(query.data?.parties ?? []);
  const totalComplete = parties.filter(
    (p) => p.setupStatus === "complete",
  ).length;
  const allComplete = parties.length > 0 && totalComplete === parties.length;

  return (
    <div className="flex flex-col gap-space-3" data-testid="closing-step-2">
      <Card>
        <header className="flex items-start justify-between gap-space-3">
          <div>
            <h3 className="font-display text-h4 text-onyx m-0">
              Step 2 · Payment Setup Status
            </h3>
            <p className="text-caption text-gray-mid m-0">
              Each party sets up their own bank details in the portal (a Stripe
              ACH mandate or payout account). This is a status view — track
              setup and nudge with a reminder; it does not gate loan creation.
            </p>
          </div>
          {allComplete ? (
            <Badge status="gold">All 3 parties set up</Badge>
          ) : (
            <Badge status="archived">
              {totalComplete} / {PARTY_ORDER.length} parties set up
            </Badge>
          )}
        </header>
      </Card>

      {/* Staff-driven servicer (TPS) decision — set apart above the party rows
          because it's Phoenix's call, not a party self-service link. */}
      <ServicingPlatformCard dealId={dealId} readOnly={readOnly} />

      {query.isLoading && (
        <Card>
          <p className="text-small text-gray-mid italic m-0">
            Loading payment setup…
          </p>
        </Card>
      )}
      {query.isError && (
        <Card>
          <p className="text-small text-status-error m-0">
            Could not load payment setup status. Refresh to retry.
          </p>
        </Card>
      )}

      {query.isSuccess &&
        parties.map((party) => (
          <PartyPaymentSetupCard
            key={party.party}
            dealId={dealId}
            party={party}
            readOnly={readOnly}
          />
        ))}
    </div>
  );
}

// Sentinel <option> value for the explicit "keep the default placeholder"
// choice (distinct from a real TPS id and from the empty no-selection value).
const KEEP_DEFAULT_VALUE = "__keep_default__";

interface ServicingPlatformCardProps {
  dealId: string;
  readOnly: boolean;
}

/** The servicer (TPS) decision block that sits ABOVE the party payment rows.
 *  Staff pick the certified servicer this loan will board onto, or explicitly
 *  keep the default placeholder for now. An explicit decision is required
 *  before Step 4 (Create Loan) unlocks. */
function ServicingPlatformCard({ dealId, readOnly }: ServicingPlatformCardProps) {
  const toast = useToast();
  const { dateTime } = useDateFormatter();
  const assignmentQuery = useDealTpsAssignment(dealId);
  const optionsQuery = useDealTpsOptions(dealId);
  const setTps = useSetDealTps(dealId);

  const assignment = assignmentQuery.data;
  const locked = readOnly || assignment?.locked === true;

  // Draft picker value. "" = nothing chosen yet; KEEP_DEFAULT_VALUE = keep
  // default; anything else = a TPS id. Re-synced whenever the recorded decision
  // changes (e.g. after a save re-fetches).
  const [selection, setSelection] = useState("");
  useEffect(() => {
    if (!assignment) return;
    setSelection(
      assignment.decision === "selected" && assignment.tpsId
        ? assignment.tpsId
        : assignment.decision === "keep_default"
          ? KEEP_DEFAULT_VALUE
          : "",
    );
  }, [assignment?.decision, assignment?.tpsId]);

  const onSave = async () => {
    if (!selection) {
      toast.error("Choose a servicer, or keep the default, before saving.");
      return;
    }
    try {
      if (selection === KEEP_DEFAULT_VALUE) {
        await setTps.mutateAsync({ decision: "keep_default" });
      } else {
        await setTps.mutateAsync({ decision: "selected", tpsId: selection });
      }
      toast.success("Servicer decision recorded.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const onClearDecision = async () => {
    try {
      await setTps.mutateAsync({ decision: "undecided" });
      toast.success("Servicer decision cleared.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const options = optionsQuery.data ?? [];

  return (
    <Card data-testid="closing-step-2-tps">
      <header className="flex items-start justify-between gap-space-3 mb-space-3">
        <div className="min-w-0 flex items-start gap-space-2">
          <Server size={18} className="text-gold-shadow shrink-0 mt-space-1" aria-hidden />
          <div className="min-w-0">
            <h4 className="font-display text-h4 text-onyx m-0">
              Servicing Platform (TPS)
            </h4>
            <p className="text-caption text-gray-mid m-0">
              Assign the certified servicer this loan will board onto, or keep
              the default for now. A decision is required before the loan can be
              created.
            </p>
          </div>
        </div>
        <TpsDecisionBadge assignment={assignment} loading={assignmentQuery.isLoading} />
      </header>

      {/* Current decision state */}
      <div data-testid="tps-decision-state">
        {assignmentQuery.isLoading ? (
          <p className="text-small text-gray-mid italic m-0">
            Loading servicer decision…
          </p>
        ) : assignmentQuery.isError ? (
          <p className="text-small text-status-error m-0">
            Could not load the servicer decision. Refresh to retry.
          </p>
        ) : assignment?.decision === "selected" ? (
          <div className="text-small text-onyx m-0">
            <span className="font-medium">
              {assignment.tpsName ?? "Selected servicer"}
            </span>
            {(assignment.decidedByUserName || assignment.decidedAt) && (
              <p className="text-caption text-gray-mid m-0 mt-space-1">
                Decided
                {assignment.decidedByUserName
                  ? ` by ${assignment.decidedByUserName}`
                  : ""}
                {assignment.decidedAt ? ` · ${dateTime(assignment.decidedAt)}` : ""}
              </p>
            )}
          </div>
        ) : assignment?.decision === "keep_default" ? (
          <div className="text-small text-onyx m-0">
            Keeping the default placeholder (no servicer yet).
            {(assignment.decidedByUserName || assignment.decidedAt) && (
              <p className="text-caption text-gray-mid m-0 mt-space-1">
                Decided
                {assignment.decidedByUserName
                  ? ` by ${assignment.decidedByUserName}`
                  : ""}
                {assignment.decidedAt ? ` · ${dateTime(assignment.decidedAt)}` : ""}
              </p>
            )}
          </div>
        ) : (
          <p className="text-small text-status-warning m-0">
            No decision yet — required before creating the loan.
          </p>
        )}
      </div>

      {/* Control — hidden once locked (deal Closed / decision frozen). */}
      {!locked && !assignmentQuery.isLoading && (
        <div
          className="mt-space-3 flex flex-col gap-space-2"
          data-testid="tps-decision-control"
        >
          <label className="flex flex-col gap-space-1">
            <span className="text-caption text-gray-mid uppercase tracking-label">
              Servicer
            </span>
            <Select
              value={selection}
              onChange={(e) => setSelection(e.target.value)}
              disabled={setTps.isPending || optionsQuery.isLoading}
              data-testid="tps-decision-select"
            >
              <option value="">
                {optionsQuery.isLoading ? "Loading servicers…" : "Choose a servicer…"}
              </option>
              <option value={KEEP_DEFAULT_VALUE}>Keep default (no TPS yet)</option>
              {options.map((o) => (
                <option key={o.tpsId} value={o.tpsId}>
                  {o.name}
                  {o.isReady ? "" : " — onboarding pending"}
                </option>
              ))}
            </Select>
          </label>
          <div className="flex items-center gap-space-2 flex-wrap">
            <Button
              variant="primary"
              size="sm"
              onClick={() => void onSave()}
              disabled={setTps.isPending || !selection}
              data-testid="tps-decision-save"
            >
              {setTps.isPending ? "Saving…" : "Save decision"}
            </Button>
            {assignment && assignment.decision !== "undecided" && (
              <Button
                variant="text"
                size="sm"
                onClick={() => void onClearDecision()}
                disabled={setTps.isPending}
                data-testid="tps-decision-clear"
              >
                Clear decision
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

/** Decision-state chip for the servicer block header. */
function TpsDecisionBadge({
  assignment,
  loading,
}: {
  assignment: DealTpsAssignment | undefined;
  loading: boolean;
}) {
  if (loading || !assignment) {
    return <StatusBadge tone="neutral">Loading…</StatusBadge>;
  }
  switch (assignment.decision) {
    case "selected":
      return <StatusBadge tone="ok">Servicer selected</StatusBadge>;
    case "keep_default":
      return <StatusBadge tone="info">Keeping default</StatusBadge>;
    default:
      return <StatusBadge tone="warn">Decision required</StatusBadge>;
  }
}

/** Deterministic Borrower → Seller → Broker order regardless of server order. */
function orderParties(
  rows: DealPaymentSetupPartyDto[],
): DealPaymentSetupPartyDto[] {
  const byParty = new Map(rows.map((r) => [r.party, r]));
  return PARTY_ORDER.map((p) => byParty.get(p)).filter(
    (r): r is DealPaymentSetupPartyDto => !!r,
  );
}

interface PartyPaymentSetupCardProps {
  dealId: string;
  party: DealPaymentSetupPartyDto;
  readOnly: boolean;
}

function PartyPaymentSetupCard({
  dealId,
  party,
  readOnly,
}: PartyPaymentSetupCardProps) {
  const toast = useToast();
  const { dateTime } = useDateFormatter();
  const relink = useRelinkDealPartyAccount(dealId);
  const remind = useRemindDealParty(dealId);
  const [linkOpen, setLinkOpen] = useState(false);

  const { linkedAccount, setupStatus } = party;
  const status = STATUS_META[setupStatus];
  const isComplete = setupStatus === "complete";
  const hasOverride = linkedAccount?.source === "override";

  const onRevert = async () => {
    try {
      await relink.mutateAsync({ party: party.party, portalUserId: null });
      toast.success(`${party.party} reverted to the inherited account.`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const onRemind = async () => {
    try {
      await remind.mutateAsync(party.party);
      toast.success("Reminder sent.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Card data-testid={`payment-setup-${party.party}`}>
      <header className="flex items-start justify-between gap-space-3 mb-space-3">
        <div className="min-w-0">
          <h4 className="font-display text-h4 text-onyx m-0">{party.party}</h4>
          {isComplete && party.completedAt && (
            <p className="text-caption text-gray-mid m-0">
              Set up on {dateTime(party.completedAt)}.
            </p>
          )}
        </div>
        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
      </header>

      {/* Linked portal account */}
      <div className="flex items-start justify-between gap-space-3 flex-wrap">
        <div className="min-w-0">
          {linkedAccount ? (
            <p className="text-small text-onyx m-0 flex items-center gap-space-2">
              <span className="font-medium whitespace-nowrap">
                {linkedAccount.fullName ?? "Unnamed account"}
              </span>
              {linkedAccount.email && (
                <span className="text-gray-mid truncate">
                  {linkedAccount.email}
                </span>
              )}
              <StatusBadge tone="neutral">{linkedAccount.source}</StatusBadge>
            </p>
          ) : (
            <p className="text-small text-gray-mid m-0">
              No {party.party} account linked.
            </p>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-space-2">
            {hasOverride && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void onRevert()}
                disabled={relink.isPending}
                data-testid={`payment-setup-revert-${party.party}`}
              >
                <RotateCcw size={14} aria-hidden /> Revert to inherited
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setLinkOpen(true)}
              data-testid={`payment-setup-relink-${party.party}`}
            >
              <Pencil size={14} aria-hidden />{" "}
              {linkedAccount ? "Change / re-link" : "Link account"}
            </Button>
          </div>
        )}
      </div>

      {/* Stripe-sourced masked payment method (complete only) */}
      {isComplete && (party.bankName || party.accountLast4) && (
        <div
          className="mt-space-3 flex items-center gap-space-2 text-small text-onyx"
          data-testid={`payment-setup-method-${party.party}`}
        >
          {party.methodLabel && (
            <span className="text-gray-mid">{party.methodLabel}</span>
          )}
          {party.bankName && <span className="font-medium">{party.bankName}</span>}
          {party.accountLast4 && (
            <span className="text-gray-mid">••••{party.accountLast4}</span>
          )}
        </div>
      )}

      {/* Reminder (incomplete + reminder-eligible) */}
      {!isComplete && party.canRemind && !readOnly && (
        <div className="mt-space-3 flex items-center gap-space-2 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void onRemind()}
            disabled={remind.isPending}
            data-testid={`payment-setup-remind-${party.party}`}
          >
            <Bell size={14} aria-hidden />{" "}
            {remind.isPending ? "Sending…" : "Send reminder"}
          </Button>
          {party.lastReminderSentAt && (
            <span className="text-caption text-gray-mid">
              Last reminded {dateTime(party.lastReminderSentAt)}
            </span>
          )}
        </div>
      )}

      {linkOpen && (
        <RelinkAccountModal
          dealId={dealId}
          party={party.party}
          open={linkOpen}
          onClose={() => setLinkOpen(false)}
        />
      )}
    </Card>
  );
}

function RelinkAccountModal({
  dealId,
  party,
  open,
  onClose,
}: {
  dealId: string;
  party: DealPaymentSetupParty;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [portalUserId, setPortalUserId] = useState("");
  const relink = useRelinkDealPartyAccount(dealId);

  const accountType = PARTY_ACCOUNT_TYPE[party];
  const accountsQuery = useQuery({
    queryKey: dealFlowKeys.paymentSetupAccountPicker(party, search),
    queryFn: () => portalAccountsApi.list({ accountType, search, limit: 20 }),
  });
  const accounts = accountsQuery.data?.items ?? [];

  const onSubmit = async () => {
    if (!portalUserId) {
      toast.error("Pick a portal account from the list.");
      return;
    }
    try {
      await relink.mutateAsync({ party, portalUserId });
      toast.success(`${party} account re-linked.`);
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Re-link ${party.toLowerCase()} portal account`}
      description={`Pick the ${party.toLowerCase()}'s portal account for payment setup. This overrides the deal-inherited account.`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={!portalUserId || relink.isPending}
            data-testid="payment-setup-relink-submit"
          >
            Re-link account
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="payment-setup-relink-form">
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">
            Find portal account
          </span>
          <input
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="payment-setup-relink-search"
          />
        </label>

        <div
          className="max-h-48 overflow-y-auto border border-border-light rounded-md"
          data-testid="payment-setup-relink-list"
        >
          {accountsQuery.isLoading && (
            <p className="text-caption text-gray-mid italic p-space-2 m-0">
              Loading accounts…
            </p>
          )}
          {accountsQuery.isError && (
            <p className="text-caption text-status-error p-space-2 m-0">
              Could not load portal accounts.
            </p>
          )}
          {accountsQuery.isSuccess && accounts.length === 0 && (
            <p className="text-caption text-gray-mid italic p-space-2 m-0">
              No portal accounts match.
            </p>
          )}
          {accounts.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setPortalUserId(a.id)}
              className={`flex w-full items-center justify-between gap-space-2 px-space-2 py-space-1 text-left hover:bg-white-warm transition-colors ${
                portalUserId === a.id ? "bg-gold/10" : ""
              }`}
              data-testid={`payment-setup-relink-option-${a.id}`}
            >
              <span className="text-small min-w-0 truncate">
                {a.fullName}
                <span className="text-gray-mid"> · {a.email}</span>
              </span>
              <span className="text-caption text-gray-mid whitespace-nowrap">
                {a.accountType}
              </span>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
